// One Process run of a batch, as a pure reducer: useProcessRun drives it,
// ProcessModal and the batch page draw it.
//
// POST /:batchId/process answers with an SSE stream (backoffice-service
// enquiry_import.controller processBatch + enquiry_import.process runBatch):
//   start     { batch_id, batch_no, run_id, total_ready, direct_ready,
//               invite_ready, skipped_incomplete }
//   progress  { current, total, row_id, row_no, flow: direct | invite,
//               status: created | failed | skipped, code?, error?,
//               enquiry_id?, reference_number?, enquiry_status?,
//               quota_hold_reason?, funding?, dispatched? }   one per row
//   result    { batch_id, status: open | completed, created, funded,
//               pending_payment, parked, canceled, failed, skipped,
//               sent_to_clik, dispatch_deferred, remaining_incomplete,
//               remaining_ready }
//   error     { code, message }: no_subscription / inactive = the run stopped
//               early (+ the result's counts; rows not reached stay ready),
//               taken_over = another run took the batch, internal = crashed
// or refuses before the stream with a JSON error (errors.processRefusal).
//
// Phases:
//   idle -> confirm -> starting -> streaming -> done | stopped | failed
//                         \-> refused (a pre-check said no)
//   starting | streaming -> watching: 409 processing (another run holds the
//     batch), taken_over, or the stream ended / dropped without its last
//     event; the run goes on on the server and the page polls GET /:batchId
//   watching -> settled: the batch is no longer processing (or its run went
//     stale: Process takes it over)
//   confirm | done | stopped | failed | refused | settled -> idle (close)
// The page reads the batch again whenever a run ends: GET /:batchId is the
// truth, never the counts here.
import { processRefusal } from './errors'

export const LIVE_PHASES = ['starting', 'streaming', 'watching']
export const TERMINAL_PHASES = ['done', 'stopped', 'failed', 'refused', 'settled']

/** The run is going on (on the server, or being checked for). */
export const isLivePhase = (phase) => LIVE_PHASES.includes(phase)
/** The run ended; its outcome waits to be looked at (close -> idle). */
export const isTerminalPhase = (phase) => TERMINAL_PHASES.includes(phase)

// `error` codes that stop a run early without breaking it (the counts are real).
const STOP_CODES = ['no_subscription', 'inactive']

// The log keeps every failed / skipped row and the last created ones.
export const LOG_CREATED_KEPT = 200

const emptyCounts = () => ({
  created: 0,
  funded: 0,
  pending_payment: 0,
  parked: 0,
  canceled: 0,
  failed: 0,
  skipped: 0,
  sent_to_clik: 0,
  dispatch_deferred: 0,
})

export const initialProcessState = Object.freeze({
  phase: 'idle',
  // Bumped when a run starts: an ending is handled once per run.
  seq: 0,
  // confirm: the batch is being read again before Process can be pressed.
  checking: false,
  recheckFailed: false,
  start: null, // the start event
  current: 0,
  total: 0,
  counts: emptyCounts(), // tallied from progress, like the backend's own
  log: [],
  loggedCreated: 0,
  summary: null, // done / stopped: the backend's counts, status, remaining
  stop: null, // stopped: { code, message }
  failure: null, // failed: { code, message }
  refusal: null, // refused: errors.processRefusal
  watch: null, // watching: 'already_processing' | 'taken_over' | 'connection_lost'
  processing: null, // 409 processing: { started_at, heartbeat_at }
  settled: null, // settled: { status, stale, counts } of the batch as polled
})

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

function tally(counts, p) {
  const next = { ...counts }
  if (p.status === 'created') {
    next.created += 1
    if (p.funding && next[p.funding] !== undefined) next[p.funding] += 1
    if (p.dispatched === true) next.sent_to_clik += 1
    if (p.dispatched === false) next.dispatch_deferred += 1
  } else if (p.status === 'failed') {
    next.failed += 1
  } else {
    next.skipped += 1
  }
  return next
}

const LOG_FIELDS = [
  'row_id',
  'row_no',
  'status',
  'flow',
  'code',
  'error',
  'reference_number',
  'enquiry_status',
  'quota_hold_reason',
  'funding',
  'dispatched',
]

function withLogEntry(state, p) {
  const entry = {}
  LOG_FIELDS.forEach((field) => {
    if (p[field] !== undefined) entry[field] = p[field]
  })
  let log = [...state.log, entry]
  let loggedCreated = state.loggedCreated + (entry.status === 'created' ? 1 : 0)
  if (loggedCreated > LOG_CREATED_KEPT) {
    const oldest = log.findIndex((e) => e.status === 'created')
    log = [...log.slice(0, oldest), ...log.slice(oldest + 1)]
    loggedCreated -= 1
  }
  return { log, loggedCreated }
}

const SUMMARY_FIELDS = [
  'status',
  'created',
  'funded',
  'pending_payment',
  'parked',
  'canceled',
  'failed',
  'skipped',
  'sent_to_clik',
  'dispatch_deferred',
  'remaining_incomplete',
  'remaining_ready',
]

function summaryOf(event) {
  const out = {}
  SUMMARY_FIELDS.forEach((field) => {
    out[field] = field === 'status' ? event.status || null : num(event[field])
  })
  return out
}

function onEvent(state, event) {
  switch (event.event) {
    case 'start':
      return {
        ...state,
        phase: 'streaming',
        start: {
          batch_no: event.batch_no || null,
          run_id: event.run_id || null,
          total_ready: num(event.total_ready),
          direct_ready: num(event.direct_ready),
          invite_ready: num(event.invite_ready),
          skipped_incomplete: num(event.skipped_incomplete),
        },
        total: num(event.total_ready),
      }
    case 'progress':
      return {
        ...state,
        phase: 'streaming',
        current: num(event.current) || state.current + 1,
        total: num(event.total) || state.total,
        counts: tally(state.counts, event),
        ...withLogEntry(state, event),
      }
    case 'result':
      return { ...state, phase: 'done', summary: summaryOf(event) }
    case 'error':
      if (STOP_CODES.includes(event.code)) {
        return {
          ...state,
          phase: 'stopped',
          summary: summaryOf(event),
          stop: { code: event.code, message: event.message || '' },
        }
      }
      if (event.code === 'taken_over') return { ...state, phase: 'watching', watch: 'taken_over' }
      return { ...state, phase: 'failed', failure: { code: event.code || 'internal', message: event.message || '' } }
    default:
      return state
  }
}

/**
 * @param {object} state
 * @param {{ type: string }} action
 *   open               (re)open the confirm step; the batch is being read again
 *   checked { ok }     that read ended (ok: false = it failed; the page's copy is shown)
 *   back               leave the confirm step
 *   begin              Process pressed
 *   event { event }    one SSE event
 *   ended              the stream ended (a 2xx body read to its end)
 *   rejected { error } the request failed (a refusal before the stream, or the network)
 *   polled { batch }   GET /:batchId while watching
 *   close              the ending was looked at
 *   reset              another batch
 */
export function processReducer(state, action) {
  switch (action?.type) {
    case 'open':
      if (isLivePhase(state.phase)) return state
      return { ...initialProcessState, seq: state.seq, phase: 'confirm', checking: true }
    case 'checked':
      if (state.phase !== 'confirm') return state
      return { ...state, checking: false, recheckFailed: !action.ok }
    case 'back':
      return state.phase === 'confirm' ? { ...initialProcessState, seq: state.seq } : state
    case 'begin':
      if (state.phase !== 'confirm') return state
      return { ...initialProcessState, seq: state.seq + 1, phase: 'starting' }
    case 'event': {
      const { event } = action
      if (!event || typeof event !== 'object') return state
      // Only a stream being read; anything after its ending is late.
      if (state.phase !== 'starting' && state.phase !== 'streaming') return state
      return onEvent(state, event)
    }
    case 'ended':
      // No result / error came: the run goes on (or ended) on the server.
      if (state.phase !== 'starting' && state.phase !== 'streaming') return state
      return { ...state, phase: 'watching', watch: 'connection_lost' }
    case 'rejected': {
      if (state.phase === 'streaming') {
        // After start the answer was a 2xx: only the connection can fail now.
        return { ...state, phase: 'watching', watch: 'connection_lost' }
      }
      if (state.phase !== 'starting') return state
      const refusal = processRefusal(action.error)
      if (refusal.code === 'processing') {
        return { ...state, phase: 'watching', watch: 'already_processing', processing: refusal.extras.processing || null }
      }
      return { ...state, phase: 'refused', refusal }
    }
    case 'polled': {
      const { batch } = action
      if (state.phase !== 'watching' || !batch) return state
      const stale = batch.status === 'processing' && Boolean(batch.processing?.stale)
      if (batch.status === 'processing' && !stale) return state
      return { ...state, phase: 'settled', settled: { status: batch.status, stale, counts: batch.counts || null } }
    }
    case 'close':
      if (state.phase !== 'confirm' && !isTerminalPhase(state.phase)) return state
      return { ...initialProcessState, seq: state.seq }
    case 'reset':
      return { ...initialProcessState, seq: state.seq }
    default:
      return state
  }
}
